import React from 'react';
import { Building2, MapPin, Briefcase, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { JobRecommendation } from '../services/recommendationsApi';

interface JobMatchCardProps {
    job: JobRecommendation;
    isSelected: boolean;
    onToggle: (job: JobRecommendation) => void;
}

const JobMatchCard: React.FC<JobMatchCardProps> = ({
    job,
    isSelected,
    onToggle
}) => {
    const getMatchScoreColor = (score: number) => {
        if (score >= 0.8) return 'bg-green-500';
        if (score >= 0.6) return 'bg-yellow-500';
        return 'bg-orange-500';
    };

    const getMatchScoreBg = (score: number) => {
        if (score >= 0.8) return 'bg-green-50 border-green-200 text-green-700';
        if (score >= 0.6) return 'bg-yellow-50 border-yellow-200 text-yellow-700';
        return 'bg-orange-50 border-orange-200 text-orange-700';
    };

    const matchPercentage = Math.round(job.match_score * 100);

    return (
        <motion.div
            whileTap={{ scale: 0.98 }}
            onClick={() => onToggle(job)}
            className={`
                relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200
                ${isSelected
                    ? 'border-[#015FA6] bg-blue-50/50 shadow-sm'
                    : 'border-[#EDEDED] bg-white hover:border-[#015FA6]/30 hover:shadow-sm'
                }
            `}
        >
            {/* Checkbox */}
            <div className={`
                absolute top-4 left-4 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200
                ${isSelected
                    ? 'bg-[#015FA6] border-[#015FA6]'
                    : 'border-[#CBCBCB] bg-white'
                }
            `}>
                {isSelected && <Check size={14} className="text-white" />}
            </div>

            <div className="pl-8">
                {/* Job Title and Company */}
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <Building2 size={16} className="text-[#015FA6] flex-shrink-0" />
                            <h4 className="text-sm font-semibold text-[#0B111E] truncate">
                                {job.title}
                            </h4>
                        </div>
                        <p className="text-sm text-[#515151] truncate pl-6">
                            {job.company_name}
                        </p>
                    </div>

                    {/* Match Score Badge */}
                    <div className={`
                        px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0
                        ${getMatchScoreBg(job.match_score)}
                    `}>
                        {matchPercentage}% match
                    </div>
                </div>

                {/* Location and Employment Type */}
                <div className="flex items-center gap-4 text-xs text-[#757575] mb-3 pl-6">
                    <span className="inline-flex items-center gap-1">
                        <MapPin size={12} />
                        {job.location || 'Remote'}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <Briefcase size={12} />
                        {job.employment_type || 'Full-time'}
                    </span>
                </div>

                {/* Match Score Progress Bar */}
                <div className="pl-6">
                    <div className="h-1.5 bg-[#F1F3F4] rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${matchPercentage}%` }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                            className={`h-full rounded-full ${getMatchScoreColor(job.match_score)}`}
                        />
                    </div>
                </div>

                {/* Match Reasons (if available) */}
                {job.match_reasons && job.match_reasons.length > 0 && (
                    <div className="mt-2 pl-6">
                        <div className="flex flex-wrap gap-1">
                            {job.match_reasons.slice(0, 2).map((reason, index) => (
                                <span
                                    key={index}
                                    className="text-xs text-[#515151] bg-[#F1F3F4] px-2 py-0.5 rounded"
                                >
                                    {reason}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default JobMatchCard;
